import {apiRequest, paginatedApiRequest} from './api';
import type {SortDirection} from './activities';
import {adminRequest, readableMessage, requireData} from './adminRequest';
import {
  adminUserEndpoint,
  adminUsersEndpoint,
  adminUserSettingsEndpoint,
  adminUserStatsEndpoint,
  signUpEndpoint,
  staticPreferencesEndpoint,
} from '../constants/endpoints';

export enum UserRole {
  Personal = 1,
  Business = 2,
  Admin = 3,
}

export interface AdminUser {
  id: string;
  fullName: string;
  email: string;
  roleCode?: number | null;
  isConfigured: boolean;
  locationCode?: number | null;
  profilePhoto?: string | null;
  // Null for an old account the auth provider doesn't know.
  createdAtUtc?: string | null;
  // Set when the user owns a business account.
  organizerName?: string | null;
}

export type AdminUserSortBy = 'createdAt' | 'name' | 'email' | 'role';

export interface AdminUserSort {
  sortBy: AdminUserSortBy;
  sortDirection: SortDirection;
}

export interface AdminUserStats {
  totalUsers: number;
  // Null when the auth provider could not be reached.
  newUsersLastWeek?: number | null;
}

export interface AdminUsersPage {
  items: AdminUser[];
  totalItems: number;
  maxPage: number;
}

export interface CreateUserInput {
  fullName: string;
  email: string;
  password: string;
}

const emptyPage: AdminUsersPage = {items: [], totalItems: 0, maxPage: 0};

// apiRequest swallows errors and returns null, which is also what a successful
// empty response looks like, so failures are tracked through onError.
const failOnError = (message: string) => {
  const state = {error: null as Error | null};
  return {
    state,
    options: {
      errorMessage: message,
      onError: (error: unknown) => {
        // The server's own message (e.g. "User already registered"), not its JSON.
        state.error = new Error(readableMessage(error, message));
      },
    },
  };
};

export const fetchAdminUsers = async (
  keyword: string,
  pageNumber: number,
  pageSize: number,
  sort?: AdminUserSort,
): Promise<AdminUsersPage> => {
  const {state, options} = failOnError('Failed to fetch users');

  const response = await paginatedApiRequest<AdminUser[]>(
    {
      endpoint: adminUsersEndpoint,
      method: 'GET',
      queryParams: {
        keyword: keyword || undefined,
        pageNumber,
        pageSize,
        ...sort,
      },
    },
    options,
  );

  if (state.error) {
    throw state.error;
  }

  // An empty result does not come back as a paginated payload.
  if (!response) {
    return emptyPage;
  }

  return {
    items: response.data ?? [],
    totalItems: response.totalItems,
    maxPage: response.maxPage,
  };
};

export const fetchAdminUserStats = async (): Promise<AdminUserStats> => {
  const {state, options} = failOnError('Failed to fetch user statistics');

  const response = await apiRequest<AdminUserStats>(
    {endpoint: adminUserStatsEndpoint, method: 'GET'},
    options,
  );

  if (state.error || !response?.data) {
    throw state.error ?? new Error('Failed to fetch user statistics');
  }

  return response.data;
};

// New accounts go through the regular sign-up, so the user receives the usual
// confirmation email. Only personal accounts can be created from here.
export const createUser = async (input: CreateUserInput): Promise<void> => {
  const {state, options} = failOnError('Failed to create user');

  await apiRequest(
    {
      endpoint: signUpEndpoint,
      method: 'POST',
      body: {
        fullName: input.fullName,
        email: input.email.toLowerCase(),
        password: input.password,
        userPreference: {roleCode: UserRole.Personal},
      },
    },
    options,
  );

  if (state.error) {
    throw state.error;
  }
};

export const updateUserName = async (
  id: string,
  fullName: string,
): Promise<void> => {
  const {state, options} = failOnError('Failed to update user');

  await apiRequest(
    {endpoint: adminUserEndpoint(id), method: 'PATCH', body: {fullName}},
    options,
  );

  if (state.error) {
    throw state.error;
  }
};

export const updateUserPhoto = async (
  id: string,
  profilePhoto: string,
): Promise<void> => {
  const {state, options} = failOnError('Failed to update the photo');

  await apiRequest(
    {endpoint: adminUserEndpoint(id), method: 'PATCH', body: {profilePhoto}},
    options,
  );

  if (state.error) {
    throw state.error;
  }
};

export const deleteUser = async (id: string): Promise<void> => {
  const {state, options} = failOnError('Failed to delete user');

  await apiRequest(
    {endpoint: adminUserEndpoint(id), method: 'DELETE'},
    options,
  );

  if (state.error) {
    throw state.error;
  }
};

export interface AdminUserSettings {
  id: string;
  // Shown only: the user changes these themselves.
  fullName: string;
  email: string;
  profilePhoto?: string | null;

  roleCode?: number | null;
  isConfigured: boolean;
  birthDate?: string | null;
  // Home city.
  locationCode?: number | null;
  friendCode?: string | null;
  hasDeviceToken: boolean;
  organizerName?: string | null;

  // False when the user never finished onboarding.
  hasPreferences: boolean;
  travelDistanceCodes: number[];
  activityTimeCodes: number[];
  vibeCodes: number[];
  categoryCodes: number[];
  tagCodes: number[];
}

export const roleLabels: Record<number, string> = {
  [UserRole.Personal]: 'Personal',
  [UserRole.Business]: 'Business',
  [UserRole.Admin]: 'Admin',
};

// Fields left out keep their value; a list that is sent replaces the stored one.
// The role is shown only: it isn't changed from the admin.
export interface UpdateUserSettingsInput {
  isConfigured?: boolean;
  birthDate?: string;
  locationCode?: number;
  travelDistanceCodes?: number[];
  activityTimeCodes?: number[];
  vibeCodes?: number[];
  categoryCodes?: number[];
  tagCodes?: number[];
}

export interface CodeName {
  code: number;
  name: string;
}

export interface StaticPreferences {
  vibes: CodeName[];
  activityTimes: CodeName[];
  travelDistances: CodeName[];
  roles: CodeName[];
}

export const fetchUserSettings = async (
  id: string,
): Promise<AdminUserSettings> =>
  requireData(
    await adminRequest<AdminUserSettings>(
      {endpoint: adminUserSettingsEndpoint(id), method: 'GET'},
      'Failed to fetch the user settings',
    ),
    'Failed to fetch the user settings',
  );

export const updateUserSettings = async (
  id: string,
  input: UpdateUserSettingsInput,
): Promise<AdminUserSettings> =>
  requireData(
    await adminRequest<AdminUserSettings, UpdateUserSettingsInput>(
      {endpoint: adminUserSettingsEndpoint(id), method: 'PATCH', body: input},
      'Failed to save the user settings',
    ),
    'Failed to save the user settings',
  );

export const fetchStaticPreferences = async (): Promise<StaticPreferences> =>
  requireData(
    await adminRequest<StaticPreferences>(
      {endpoint: staticPreferencesEndpoint, method: 'GET'},
      'Failed to fetch the preference options',
    ),
    'Failed to fetch the preference options',
  );
